<!-- Dashboard Overview -->
<div class="dashboard">
  <!-- Header -->
  <div class="dash-header">
    <div>
      <h1 class="dash-title">Dashboard</h1>
      <p class="dash-subtitle">Monitor your business performance and key metrics</p>
    </div>
    <div class="dash-actions">
      <div class="filter-pills">
        <button class="pill" [class.active]="dateFilter === 'all'" (click)="setDateFilter('all')">All Time</button>
        <button class="pill" [class.active]="dateFilter === 'today'" (click)="setDateFilter('today')">Today</button>
        <button class="pill" [class.active]="dateFilter === 'week'" (click)="setDateFilter('week')">This Week</button>
        <button class="pill" [class.active]="dateFilter === 'month'" (click)="setDateFilter('month')">This Month</button>
        <button class="pill" [class.active]="dateFilter === 'custom'" (click)="setDateFilter('custom')">Custom</button>
      </div>
      <button class="btn btn-outline-secondary btn-sm" (click)="refreshData()">
        <i class="fas fa-sync-alt"></i>
      </button>
    </div>
  </div>

  <!-- Custom Date Range -->
  @if (showDatePicker) {
    <div class="date-range-bar">
      <div class="date-inputs">
        <div class="date-field">
          <label>Start Date</label>
          <input type="date" class="form-control" [(ngModel)]="customStartDate">
        </div>
        <div class="date-field">
          <label>End Date</label>
          <input type="date" class="form-control" [(ngModel)]="customEndDate">
        </div>
        <button class="btn btn-primary" (click)="applyCustomDate()">Apply</button>
      </div>
    </div>
  }

  <!-- KPI Cards - Row 1 -->
  <div class="kpi-grid">
    <div class="kpi-card">
      <div class="kpi-top">
        <div class="kpi-icon green"><i class="fas fa-chart-line"></i></div>
      </div>
      @if (!isLoadingSummary) {
        <div class="kpi-value">{{totalSales.total_sales || 0 | currency: '₦'}}</div>
        <div class="kpi-label">Total Sales</div>
      } @else {
        <div class="shimmer-line" style="width: 80%; height: 28px; margin: 8px 0;"></div>
        <div class="shimmer-line" style="width: 50%; height: 14px;"></div>
      }
    </div>

    <div class="kpi-card">
      <div class="kpi-top">
        <div class="kpi-icon amber"><i class="fas fa-receipt"></i></div>
      </div>
      @if (!isLoadingSummary) {
        <div class="kpi-value">{{totalSales.total_charges_dota || 0 | currency: '₦'}}</div>
        <div class="kpi-label">DOTA Charges</div>
      } @else {
        <div class="shimmer-line" style="width: 80%; height: 28px; margin: 8px 0;"></div>
        <div class="shimmer-line" style="width: 50%; height: 14px;"></div>
      }
    </div>

    <div class="kpi-card">
      <div class="kpi-top">
        <div class="kpi-icon purple"><i class="fas fa-hand-holding-usd"></i></div>
      </div>
      @if (!isLoadingSummary) {
        <div class="kpi-value">{{(totalSales.total_sales - totalSales.total_charges_dota) || 0 | currency: '₦'}}</div>
        <div class="kpi-label">Total Remitted</div>
      } @else {
        <div class="shimmer-line" style="width: 80%; height: 28px; margin: 8px 0;"></div>
        <div class="shimmer-line" style="width: 50%; height: 14px;"></div>
      }
    </div>

    <div class="kpi-card">
      <div class="kpi-top">
        <div class="kpi-icon red"><i class="fas fa-money-bill-wave"></i></div>
      </div>
      @if (!isLoadingSummary) {
        <div class="kpi-value">{{totalSales.total_withdrawal || 0 | currency: '₦'}}</div>
        <div class="kpi-label">Total Withdrawal</div>
      } @else {
        <div class="shimmer-line" style="width: 80%; height: 28px; margin: 8px 0;"></div>
        <div class="shimmer-line" style="width: 50%; height: 14px;"></div>
      }
    </div>
  </div>

  <!-- KPI Cards - Row 2: Payment Methods -->
  <div class="kpi-grid kpi-grid-3">
    <div class="kpi-card compact">
      <div class="kpi-top">
        <div class="kpi-icon blue"><i class="fas fa-exchange-alt"></i></div>
      </div>
      @if (!isLoadingSummary) {
        <div class="kpi-value sm">{{totalSales.total_transfer || 0 | currency: '₦'}}</div>
        <div class="kpi-label">Transfer</div>
      } @else {
        <div class="shimmer-line" style="width: 70%; height: 22px; margin: 6px 0;"></div>
        <div class="shimmer-line" style="width: 40%; height: 12px;"></div>
      }
    </div>

    <div class="kpi-card compact">
      <div class="kpi-top">
        <div class="kpi-icon teal"><i class="fas fa-credit-card"></i></div>
      </div>
      @if (!isLoadingSummary) {
        <div class="kpi-value sm">{{totalSales.total_pos || 0 | currency: '₦'}}</div>
        <div class="kpi-label">POS</div>
      } @else {
        <div class="shimmer-line" style="width: 70%; height: 22px; margin: 6px 0;"></div>
        <div class="shimmer-line" style="width: 40%; height: 12px;"></div>
      }
    </div>

    <div class="kpi-card compact">
      <div class="kpi-top">
        <div class="kpi-icon amber"><i class="fas fa-money-bill-alt"></i></div>
      </div>
      @if (!isLoadingSummary) {
        <div class="kpi-value sm">{{totalSales.total_cash || 0 | currency: '₦'}}</div>
        <div class="kpi-label">Cash</div>
      } @else {
        <div class="shimmer-line" style="width: 70%; height: 22px; margin: 6px 0;"></div>
        <div class="shimmer-line" style="width: 40%; height: 12px;"></div>
      }
    </div>
  </div>

  <!-- Order Modal Template -->
  <ng-template #orderModal>
    <div class="modal-header">
      <h5 class="modal-title">Order Details</h5>
      <button type="button" class="btn-close" aria-label="Close" (click)="closeModal()"></button>
    </div>
    <div class="modal-body">
      <div class="d-flex align-items-center mb-4">
        <img [src]="selectedOrder?.waiter?.image || 'profile.png'" width="48" height="48" class="rounded-circle me-3" alt="">
        <div>
          <div class="fw-semibold">{{ selectedOrder?.waiter?.first_name }} {{ selectedOrder?.waiter?.last_name }}</div>
          <div class="text-muted" style="font-size: 12px;">{{ selectedOrder?.waiter?.email }}</div>
        </div>
      </div>

      <table class="table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th class="text-end">Price</th>
          </tr>
        </thead>
        <tbody>
          @for (item of selectedOrder?.order_items; track item) {
            <tr>
              <td>
                <div class="d-flex align-items-center">
                  <img [src]="item.image" width="36" height="36" class="rounded me-2" alt="">
                  {{ item.inventory }}
                </div>
              </td>
              <td>{{ item.quantity }}</td>
              <td class="text-end">{{ item.amount | currency: '₦' }}</td>
            </tr>
          }
        </tbody>
      </table>

      <div class="d-flex justify-content-between align-items-center mt-3 pt-3" style="border-top: 1px solid var(--border);">
        <span class="fw-semibold">Total</span>
        <span class="fw-bold" style="font-size: 18px;">{{ getTotalOrderAmount() | currency: '₦' }}</span>
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-danger" (click)="closeOrder(selectedOrder.uid)">Close Order</button>
    </div>
  </ng-template>

  <!-- Content Grid -->
  <div class="content-grid">
    <!-- Inventory Level -->
    <div class="dash-card">
      <div class="dash-card-header">
        <h3 class="dash-card-title">Inventory Level</h3>
      </div>
      <div class="dash-card-body scrollable">
        <table class="table">
          <thead>
            <tr>
              <th>Item</th>
              <th class="text-end">Qty</th>
            </tr>
          </thead>
          <tbody>
            @for (inv of inventories(); track inv.id) {
              <tr [hidden]="isLoadingInventory">
                <td>
                  <div class="d-flex align-items-center">
                    <img [src]="inv?.main_image || 'profile.png'" width="36" height="36" class="rounded me-2" alt="">
                    <span>{{ inv?.name }}</span>
                  </div>
                </td>
                <td class="text-end">
                  <span class="qty-badge"
                        [class.critical]="inv?.quantity <= 5"
                        [class.low]="inv?.quantity > 5 && inv?.quantity <= 25"
                        [class.ok]="inv?.quantity > 25">
                    {{ inv?.quantity }}
                  </span>
                </td>
              </tr>
            }
            @for (i of [1,2,3,4,5]; track i) {
              <tr [hidden]="!isLoadingInventory">
                <td><div class="d-flex align-items-center"><div class="loading-shimmer rounded me-2" style="width: 36px; height: 36px;"></div><div class="loading-shimmer" style="width: 100px; height: 14px;"></div></div></td>
                <td class="text-end"><div class="loading-shimmer ms-auto" style="width: 36px; height: 14px;"></div></td>
              </tr>
            }
            @empty {
              <tr [hidden]="isLoadingInventory">
                <td colspan="2">
                  <div class="empty-state py-4">
                    <p class="text-muted mb-2">No inventory items</p>
                    <button class="btn btn-primary btn-sm" (click)="addInv()">Add Inventory</button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pending Orders -->
    <div class="dash-card">
      <div class="dash-card-header">
        <h3 class="dash-card-title">Pending Orders</h3>
      </div>
      <div class="dash-card-body scrollable">
        @for (item of pendingOrders; track item.created_at) {
          <div class="list-item" [hidden]="isLoadingOrders">
            <div class="list-item-content">
              <div class="list-item-title">Order from {{ item?.barcode_details.name }}</div>
              <div class="list-item-meta" [title]="item.created_at | date:'medium'">{{ item.human_readable_date }}</div>
            </div>
            <button class="btn btn-primary btn-sm" (click)="openOrderDetails(item, orderModal)">View</button>
          </div>
        }
        @for (i of [1,2,3,4]; track i) {
          <div class="list-item" [hidden]="!isLoadingOrders">
            <div class="list-item-content">
              <div class="loading-shimmer" style="width: 140px; height: 14px; margin-bottom: 4px;"></div>
              <div class="loading-shimmer" style="width: 80px; height: 12px;"></div>
            </div>
            <div class="loading-shimmer" style="width: 60px; height: 30px; border-radius: 8px;"></div>
          </div>
        }
        @empty {
          <div class="empty-state py-4" [hidden]="isLoadingOrders">
            <p class="text-muted">No pending orders</p>
          </div>
        }
      </div>
    </div>

    <!-- Active Waiters -->
    <div class="dash-card">
      <div class="dash-card-header">
        <h3 class="dash-card-title">Active Waiters</h3>
      </div>
      <div class="dash-card-body scrollable">
        @for (item of activeWaiters; track item.created_at) {
          <div class="list-item" [hidden]="isLoadingWaiters">
            <div class="d-flex align-items-center gap-3">
              <img [src]="item?.image || 'profile.png'" width="36" height="36" class="rounded-circle" alt="">
              <div class="list-item-title">{{ item?.first_name + ' ' + item?.last_name }}</div>
            </div>
            <div class="waiter-rating"><i class="fas fa-star"></i> 4.90</div>
          </div>
        }
        @for (i of [1,2,3,4]; track i) {
          <div class="list-item" [hidden]="!isLoadingWaiters">
            <div class="d-flex align-items-center gap-3">
              <div class="loading-shimmer rounded-circle" style="width: 36px; height: 36px;"></div>
              <div class="loading-shimmer" style="width: 100px; height: 14px;"></div>
            </div>
            <div class="loading-shimmer" style="width: 50px; height: 14px;"></div>
          </div>
        }
        @empty {
          <div class="empty-state py-4" [hidden]="isLoadingWaiters">
            <p class="text-muted">No active waiters</p>
          </div>
        }
      </div>
    </div>

    <!-- Activity Logs -->
    <div class="dash-card">
      <div class="dash-card-header">
        <h3 class="dash-card-title">Activity Logs</h3>
      </div>
      <div class="dash-card-body scrollable">
        @for (item of activityLogs; track item.created_at) {
          <div class="list-item" [hidden]="isLoadingLogs">
            <div class="list-item-content">
              <div class="list-item-title">{{ item?.log }}</div>
              <div class="list-item-meta" [title]="item.created_at | date:'medium'">{{ item.human_readable_date }}</div>
            </div>
          </div>
        }
        @for (i of [1,2,3,4]; track i) {
          <div class="list-item" [hidden]="!isLoadingLogs">
            <div class="list-item-content">
              <div class="loading-shimmer" style="width: 180px; height: 14px; margin-bottom: 4px;"></div>
              <div class="loading-shimmer" style="width: 80px; height: 12px;"></div>
            </div>
          </div>
        }
        @empty {
          <div class="empty-state py-4" [hidden]="isLoadingLogs">
            <p class="text-muted">No activity logs</p>
          </div>
        }
      </div>
    </div>
  </div>
</div>
